/**
 * Per-action request-body whitelist.
 *
 * Every required request field must appear here; send_email also preserves
 * every optional field. Other actions may narrow optional fields deliberately.
 * `buildBody()` picks only these keys from the caller's `input`, so a
 * caller that spreads a larger object (e.g. a full constituent record)
 * does not leak extra fields across the wire into edge-function logs.
 *
 * The backend's zod discriminated union also strips unknown fields, but
 * only after they've been serialised, transmitted, and parsed — by which
 * point the leaked fields are already sitting in Supabase function logs.
 * This is the frontend's belt; the backend is the suspenders.
 *
 * Adding a new action: TypeScript's `satisfies Record<CorrespondenceAction,
 * readonly string[]>` is a compile-time check that every action has an
 * entry. `use-correspondence-api.body-whitelist.test.ts` checks schema parity,
 * including complete optional-field coverage for send_email.
 */

import type { CorrespondenceAction, RequestByAction } from '@/lib/correspondence/contracts';

export const WHITELIST_BY_ACTION = {
  list_messages: [
    'organizationId',
    'accountId',
    'pageToken',
    'maxResults',
    'labelIds',
    'query',
    'folder',
    'idempotencyKey',
  ],
  search_messages: [
    'organizationId',
    'accountId',
    'query',
    'maxResults',
    'pageToken',
    'idempotencyKey',
  ],
  get_message: ['organizationId', 'accountId', 'messageId', 'format', 'idempotencyKey'],
  get_thread: ['organizationId', 'accountId', 'threadId', 'idempotencyKey'],
  get_attachment: ['organizationId', 'accountId', 'messageId', 'attachmentId', 'idempotencyKey'],
  list_labels: ['organizationId', 'accountId', 'idempotencyKey'],
  get_profile: ['organizationId', 'accountId', 'idempotencyKey'],
  get_profile_photo: ['organizationId', 'accountId', 'idempotencyKey'],
  get_profile_name: ['organizationId', 'accountId', 'idempotencyKey'],
  modify_message: [
    'organizationId',
    'accountId',
    'messageId',
    'addLabels',
    'removeLabels',
    'applyToThread',
    'idempotencyKey',
  ],
  delete_message: ['organizationId', 'accountId', 'messageId', 'applyToThread', 'idempotencyKey'],
  trash_message: ['organizationId', 'accountId', 'messageId', 'idempotencyKey'],
  untrash_message: ['organizationId', 'accountId', 'messageId', 'idempotencyKey'],
  create_label: ['organizationId', 'accountId', 'name', 'color', 'idempotencyKey'],
  batch_modify_messages: [
    'organizationId',
    'accountId',
    'messageIds',
    'addLabels',
    'removeLabels',
    'applyToThread',
    'idempotencyKey',
  ],
  archive_message: ['organizationId', 'accountId', 'messageIds', 'applyToThread', 'idempotencyKey'],
  send_email: [
    'organizationId',
    'accountId',
    'to',
    'cc',
    'bcc',
    'subject',
    'bodyHtml',
    'bodyText',
    'replyToMessageId',
    'threadId',
    'references',
    // Conversation linkage the server re-verifies against the account's org
    // before recording it — never an authorization input.
    'correspondenceId',
    'sourceDraftId',
    'fromAlias',
    'fromName',
    'attachments',
    // The on-behalf bundle travels as a whole nested object — `buildBody`
    // copies the value by reference and the backend schema is `.strict()`,
    // so it is neither flattened here nor widened by omission there.
    'onBehalf',
    'idempotencyKey',
  ],
  save_draft: [
    'organizationId',
    'accountId',
    'mode',
    'referenceMessageId',
    'threadId',
    'to',
    'cc',
    'bcc',
    'subject',
    'bodyHtml',
    'idempotencyKey',
  ],
  update_draft: [
    'organizationId',
    'accountId',
    'draftId',
    'etag',
    'referenceMessageId',
    'threadId',
    'mode',
    'to',
    'cc',
    'bcc',
    'subject',
    'bodyHtml',
    'idempotencyKey',
  ],
  delete_draft: ['organizationId', 'accountId', 'draftId', 'idempotencyKey'],
  list_history: ['organizationId', 'accountId', 'cursor', 'maxResults', 'idempotencyKey'],
  get_signature: ['organizationId', 'accountId', 'idempotencyKey'],
  refresh_signature: ['organizationId', 'accountId', 'idempotencyKey'],
  refresh_send_as: ['organizationId', 'accountId', 'idempotencyKey'],
  // `fetch_message_body` does not take `accountId` — the owning account is
  // derived server-side from `email_metadata.account_id` after an org-scoped
  // lookup. See docs/solutions/feature-implementations/on-demand-email-body-recovery.md.
  fetch_message_body: ['organizationId', 'emailMetadataId', 'idempotencyKey'] as const,
  // `fetch_raw_message` mirrors `fetch_message_body`: no `accountId`, owning
  // account derived server-side from the org-scoped `email_metadata` lookup.
  fetch_raw_message: ['organizationId', 'emailMetadataId', 'idempotencyKey'] as const,
  // `get_unsubscribe_info` also keys on the `email_metadata` row (`emailId`);
  // no `accountId` — the owning account is derived server-side.
  get_unsubscribe_info: ['organizationId', 'emailId', 'idempotencyKey'] as const,
  // `unsubscribe` mirrors `get_unsubscribe_info`'s envelope: `emailId` only,
  // owning account derived server-side (ADR 0087).
  unsubscribe: ['organizationId', 'emailId', 'idempotencyKey'] as const,
  // `reset_unsubscribe` withdraws the request that same envelope identifies.
  reset_unsubscribe: ['organizationId', 'emailId', 'idempotencyKey'] as const,
  // `get_capabilities` is globally scoped — handled before any account/org
  // resolution. No request fields are accepted beyond `action`.
  get_capabilities: [] as const,
} as const satisfies Record<CorrespondenceAction, readonly string[]>;

export function buildBody<A extends CorrespondenceAction>(
  action: A,
  input: RequestByAction[A],
): Record<string, unknown> {
  const body: Record<string, unknown> = { action };
  const inputRecord = input as Record<string, unknown>;
  for (const key of WHITELIST_BY_ACTION[action]) {
    const value = inputRecord[key];
    if (value !== undefined) {
      body[key] = value;
    }
  }
  return body;
}
